import React, { useMemo, useState } from "react";
import FilteredMembersModal from "./FilteredMembersModal";
import { AGE_GROUPS, matchesAgeSub } from "./ageHelpers";
import { fullName, formatDate } from "../../lib/format";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { buildCSV, downloadCSV } from "./_utils";

// ============================================================
// Membership tab — 6 non-clickable section headers, with clickable
// metric chips that open a filtered member list. Includes the
// New Members / Family Dynamic / Baptism inline reports.
// ============================================================
const STATUS_OPTIONS  = ["Active", "New Member", "Guest", "Inactive", "Deceased", "Left Church"];
const MARITAL_OPTIONS = ["Married", "Single", "Widowed", "Divorced"];
const SPIRITUAL_OPTIONS = [
  { id: "baptized",     label: "Baptized",         match: m => !!m.baptized },
  { id: "not_baptized", label: "Not Yet Baptized", match: m => !m.baptized },
  { id: "ordained",     label: "Ordained",         match: m => !!m.ordained },
];

export default function MembershipTab({ members, families, navigate }) {
  const [filter, setFilter] = useState(null); // { title, sub, members }

  const closeFilter = () => setFilter(null);

  const openFilter = (title, sub, predicate) => {
    const list = members.filter(predicate);
    setFilter({ title, sub, members: list });
  };

  // Counts
  const byStatus = useMemo(() => {
    const map = {};
    STATUS_OPTIONS.forEach(s => { map[s] = 0; });
    members.forEach(m => { if (map[m.memberStatus] !== undefined) map[m.memberStatus] += 1; });
    return map;
  }, [members]);

  const byMarital = useMemo(() => {
    const map = {};
    MARITAL_OPTIONS.forEach(s => { map[s] = 0; });
    members.forEach(m => { if (map[m.maritalStatus] !== undefined) map[m.maritalStatus] += 1; });
    return map;
  }, [members]);

  const byLanguage = useMemo(() => {
    const map = {};
    members.forEach(m => { if (m.language) map[m.language] = (map[m.language] || 0) + 1; });
    return map;
  }, [members]);

  const byDepartment = useMemo(() => {
    const map = {};
    members.forEach(m => (m.departments || []).forEach(d => { map[d] = (map[d] || 0) + 1; }));
    return map;
  }, [members]);

  const spiritualCounts = useMemo(() => SPIRITUAL_OPTIONS.map(o => ({
    ...o, count: members.filter(o.match).length,
  })), [members]);

  // Family Dynamic counts (married / single / widowed)
  const familyDynamicTop = useMemo(() => ([
    { id: "married", label: "Married Members",  predicate: m => m.maritalStatus === "Married",  count: members.filter(m => m.maritalStatus === "Married").length },
    { id: "single",  label: "Single Members",   predicate: m => m.maritalStatus === "Single",   count: members.filter(m => m.maritalStatus === "Single").length },
    { id: "widowed", label: "Widowed Members",  predicate: m => m.maritalStatus === "Widowed",  count: members.filter(m => m.maritalStatus === "Widowed").length },
  ]), [members]);

  return (
    <div data-testid="analytics-membership-tab">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          data-testid="membership-export-csv"
          onClick={() => {
            const headers = ["First Name", "Last Name", "Status", "Marital", "Baptized", "Departments", "Email", "Phone", "Joined"];
            const rows = (members || []).map(m => [
              m.firstName || "",
              m.lastName || "",
              m.memberStatus || "",
              m.maritalStatus || "",
              m.baptized ? "Yes" : "No",
              (m.departments || []).join("; "),
              (Array.isArray(m.email) && m.email[0]?.address) || "",
              (Array.isArray(m.phone) && m.phone[0]?.number) || "",
              m.joinedDate || "",
            ]);
            downloadCSV("membership.csv", buildCSV({ headers, rows }));
          }}
        >⬇ CSV</button>
      </div>
      {/* STATUS */}
      <Section title="Status" testid="section-status">
        <ChipGrid>
          {STATUS_OPTIONS.map(s => (
            <Chip
              key={s}
              testid={`status-${s.toLowerCase().replace(/\s+/g, "-")}`}
              label={s}
              count={byStatus[s]}
              onClick={() => openFilter(`Status: ${s}`, `${byStatus[s]} member${byStatus[s] === 1 ? "" : "s"}`, m => m.memberStatus === s)}
            />
          ))}
        </ChipGrid>
      </Section>

      {/* MARITAL STATUS */}
      <Section title="Marital Status" testid="section-marital">
        <ChipGrid>
          {MARITAL_OPTIONS.map(s => (
            <Chip
              key={s}
              testid={`marital-${s.toLowerCase()}`}
              label={s}
              count={byMarital[s]}
              onClick={() => openFilter(`Marital Status: ${s}`, null, m => m.maritalStatus === s)}
            />
          ))}
        </ChipGrid>
      </Section>

      {/* LANGUAGE */}
      <Section title="Language" testid="section-language">
        {Object.keys(byLanguage).length === 0 ? (
          <Empty text="No language data available." />
        ) : (
          <ChipGrid>
            {Object.entries(byLanguage).sort((a, b) => b[1] - a[1]).map(([lang, count]) => (
              <Chip
                key={lang}
                testid={`language-${lang.toLowerCase().replace(/\s+/g, "-")}`}
                label={lang}
                count={count}
                onClick={() => openFilter(`Language: ${lang}`, null, m => m.language === lang)}
              />
            ))}
          </ChipGrid>
        )}
      </Section>

      {/* DEPARTMENT */}
      <Section title="Department" testid="section-department">
        {Object.keys(byDepartment).length === 0 ? (
          <Empty text="No members are assigned to a department yet." />
        ) : (
          <ChipGrid>
            {Object.entries(byDepartment).sort((a, b) => b[1] - a[1]).map(([dept, count]) => (
              <Chip
                key={dept}
                testid={`department-${dept.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                label={dept}
                count={count}
                onClick={() => openFilter(`Department: ${dept}`, null, m => (m.departments || []).includes(dept))}
              />
            ))}
          </ChipGrid>
        )}
      </Section>

      {/* SPIRITUAL STATUS */}
      <Section title="Spiritual Status" testid="section-spiritual">
        <ChipGrid>
          {spiritualCounts.map(o => (
            <Chip
              key={o.id}
              testid={`spiritual-${o.id}`}
              label={o.label}
              count={o.count}
              onClick={() => openFilter(`Spiritual Status: ${o.label}`, null, o.match)}
            />
          ))}
        </ChipGrid>
      </Section>

      {/* FAMILY DYNAMIC */}
      <Section title="Family Dynamic" testid="section-family-dynamic">
        <ChipGrid>
          {familyDynamicTop.map(o => (
            <Chip
              key={o.id}
              testid={`family-${o.id}`}
              label={o.label}
              count={o.count}
              onClick={() => openFilter(o.label, null, o.predicate)}
            />
          ))}
        </ChipGrid>

        <div className="age-groups">
          {AGE_GROUPS.map(g => {
            const groupTotal = g.subs.reduce((sum, s) => sum + members.filter(m => matchesAgeSub(m, s)).length, 0);
            return (
              <div key={g.id} className="age-group-card" data-testid={`age-group-${g.id}`}>
                <div className="age-group-header">
                  <div className="age-group-title">{g.label}</div>
                  <div className="age-group-meta">{g.range} · {groupTotal} member{groupTotal === 1 ? "" : "s"}</div>
                </div>
                <div className="age-group-subs">
                  {g.subs.map(s => {
                    const matched = members.filter(m => matchesAgeSub(m, s));
                    return (
                      <button
                        type="button"
                        key={s.id}
                        data-testid={`age-sub-${s.id}`}
                        className="age-sub-chip"
                        onClick={() => openFilter(s.label, `${s.range} years old`, m => matchesAgeSub(m, s))}
                      >
                        <span className="asc-label">{s.label}</span>
                        <span className="asc-range">{s.range}</span>
                        <span className="asc-count">{matched.length}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* NEW MEMBERS report */}
      <Section title="New Members Report" testid="section-new-members">
        <NewMembersInline members={members} navigate={navigate} />
      </Section>

      {/* FAMILY DYNAMIC report (newborns, marriages, deaths, departures) */}
      <Section title="Family Dynamic Report" testid="section-family-report">
        <FamilyDynamicInline members={members} families={families} />
      </Section>

      {/* BAPTISM report */}
      <Section title="Baptism Report" testid="section-baptism-report">
        <BaptismInline members={members} />
      </Section>

      {filter && (
        <FilteredMembersModal
          title={filter.title}
          sub={filter.sub}
          members={filter.members}
          onClose={closeFilter}
          navigate={navigate}
        />
      )}
    </div>
  );
}

// ----- small building blocks -----
function Section({ title, testid, children }) {
  return (
    <section className="analytics-section" data-testid={testid}>
      <h2 className="analytics-section-title">{title}</h2>
      {children}
    </section>
  );
}
function ChipGrid({ children }) {
  return <div className="analytics-chip-grid">{children}</div>;
}
function Chip({ label, count, onClick, testid }) {
  return (
    <button type="button" className="analytics-chip" data-testid={testid} onClick={onClick}>
      <span className="ac-label">{label}</span>
      <span className="ac-count">{count}</span>
    </button>
  );
}
function Empty({ text }) {
  return <div className="analytics-empty">{text}</div>;
}

// ----- inline reports -----
function NewMembersInline({ members, navigate }) {
  const [period, setPeriod] = useState("month");
  const now = new Date();
  const filtered = members.filter(m => {
    if (!m.joinedDate) return false;
    const d = new Date(m.joinedDate);
    if (period === "week") { const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7); return d >= weekAgo; }
    if (period === "month") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    if (period === "year") return d.getFullYear() === now.getFullYear();
    return true;
  }).sort((a, b) => new Date(b.joinedDate) - new Date(a.joinedDate));
  const periodLabel = period === "week" ? "This Week" : period === "month" ? "This Month" : period === "year" ? "This Year" : "All Time";

  return (
    <div className="analytics-inline-card">
      <div className="analytics-inline-toolbar">
        <div className="analytics-inline-count">{filtered.length} new member{filtered.length === 1 ? "" : "s"} — {periodLabel}</div>
        <select className="analytics-period-select" data-testid="new-members-period" value={period} onChange={e => setPeriod(e.target.value)}>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
          <option value="all">All Time</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <div className="analytics-empty">No new members in this period.</div>
      ) : (
        <div className="analytics-row-list" data-testid="new-members-list">
          {filtered.map(m => (
            <div key={m.id} role="button" tabIndex={0} className="analytics-row" data-testid={`new-members-row-${m.id}`} onClick={() => navigate(`/members/${m.id}`)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/members/${m.id}`); } }}>
              <Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{ width: 30, height: 30, fontSize: 11 }} />
              <div className="ar-main">
                <div className="ar-name">{fullName(m)}</div>
                <div className="ar-meta">Joined {formatDate(m.joinedDate)} · {m.memberStatus}</div>
              </div>
              <span className="ar-arrow" aria-hidden="true">›</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FamilyDynamicInline({ members, families }) {
  const newborns   = members.filter(m => m.additionType === "Newborn/Dedication").sort((a, b) => new Date(b.additionDate) - new Date(a.additionDate));
  const marriages  = members.filter(m => m.maritalStatus === "Married" && m.additionType === "Joined by decision").sort((a, b) => new Date(b.joinedDate) - new Date(a.joinedDate));
  const deaths     = members.filter(m => m.subtractionType === "Death").sort((a, b) => new Date(b.subtractionDate) - new Date(a.subtractionDate));
  const departures = members.filter(m => m.subtractionType && m.subtractionType !== "Death").sort((a, b) => new Date(b.subtractionDate) - new Date(a.subtractionDate));

  return (
    <div className="family-grid">
      <FamilyDynamicCard title="Newborns & Dedications" iconName="baby" rows={newborns} testid="family-newborns" families={families} dateKey="additionDate" />
      <FamilyDynamicCard title="Married Members"        iconName="wedding" rows={marriages} testid="family-marriages" families={families} dateKey="joinedDate" />
      <FamilyDynamicCard title="Deaths"                 iconName="dove" rows={deaths} testid="family-deaths" families={families} dateKey="subtractionDate" />
      <FamilyDynamicCard title="Departures"             iconName="logout" rows={departures} testid="family-departures" families={families} dateKey="subtractionDate" />
    </div>
  );
}

function FamilyDynamicCard({ title, iconName, icon, rows, testid, families, dateKey }) {
  return (
    <div className="family-card" data-testid={testid}>
      <div className="family-card-header">
        <span className="family-card-icon" aria-hidden="true">{iconName ? <CustomIcon name={iconName} size={18} /> : icon}</span>
        <span className="family-card-title">{title}</span>
        <span className="family-card-count">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="analytics-empty">No records</div>
      ) : (
        <div className="family-card-list">
          {rows.slice(0, 10).map(m => {
            const fam = m.familyId ? families.find(f => f.id === m.familyId) : null;
            return (
              <div key={m.id} className="family-row" data-testid={`${testid}-row-${m.id}`}>
                <div className="fr-name">{fullName(m)}</div>
                <div className="fr-meta">
                  {formatDate(m[dateKey])}{fam ? ` · ${fam.name}` : ""}
                </div>
              </div>
            );
          })}
          {rows.length > 10 && <div className="family-more">+ {rows.length - 10} more</div>}
        </div>
      )}
    </div>
  );
}

function BaptismInline({ members }) {
  const baptized = members.filter(m => m.baptized).sort((a, b) => new Date(a.baptizedDate || 0) - new Date(b.baptizedDate || 0));
  if (baptized.length === 0) return <div className="analytics-empty">No baptism records found.</div>;
  return (
    <div className="analytics-inline-card">
      <div className="analytics-inline-toolbar">
        <div className="analytics-inline-count">{baptized.length} baptized member{baptized.length === 1 ? "" : "s"}</div>
      </div>
      <div className="analytics-row-list" data-testid="baptism-list">
        {baptized.map(m => (
          <div key={m.id} className="analytics-row" data-testid={`baptism-row-${m.id}`}>
            <Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{ width: 30, height: 30, fontSize: 11 }} />
            <div className="ar-main">
              <div className="ar-name">{fullName(m)}</div>
              <div className="ar-meta">
                {m.baptizedDate ? formatDate(m.baptizedDate) : "Date unknown"}
                {m.baptizedBy ? ` · by ${m.baptizedBy}` : ""}
                {m.baptizedWhere ? ` · at ${m.baptizedWhere}` : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
